/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React, { ReactNode } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import type { RootState } from '../../../@types';
import SearchUtils from '../../../utils/SearchUtils';
import Carousel from '@cchome/carousel';
import './SearchDropDownQuickActions.scss';
import HorizontalRuler from '../../HorizontalRuler';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchDropDownQuickActionsItem from './SearchDropDownQuickActionsItem';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import AnalyticsUtils from '../../../analytics/AnalyticsUtil';
import {
	APP_TYPES,
	PRESEARCH_UI_SORT_ORDERS,
	SEARCH_CCD_CCH_SAMCAP_CGEN,
} from '../../../containers/SearchResultsPage/SearchConstants';

type OwnProps = {};

type SearchDropDownQuickActionsProps = OwnProps & WrappedComponentProps & PropsFromRedux;

interface State {
	areChildrenFocusable: boolean;
}

class SearchDropDownQuickActions extends ClickBase<SearchDropDownQuickActionsProps, State> {
	analytics: SearchAnalytics;
	quickActionsCarouselRef = React.createRef<HTMLDivElement>();
	constructor(props) {
		super(props);
		this.state = {
			areChildrenFocusable: false,
		};
		this.searchHostContext = SearchHostContext.getContext();
		this.analytics = new SearchAnalytics(this.searchHostContext);
		this.onCarouselClick = this.onCarouselClick.bind(this);
		this.onClickSeeAll = this.onClickSeeAll.bind(this);
	}

	componentDidMount(): void {
		const quickActionsData =
			this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.resultItems || [];
		const applicableQuicActionsData = this.getApplicableQuickActionData(quickActionsData);
		if (!applicableQuicActionsData?.length) {
			return;
		}
	}

	componentDidUpdate(prevProps: Readonly<SearchDropDownQuickActionsProps>, prevState: Readonly<State>): void {
		if (this.state.areChildrenFocusable !== prevState.areChildrenFocusable) {
			if (this.state.areChildrenFocusable) {
				const firstFocusable = document.getElementById('search-dropdown-quick-action-item-0');
				if (firstFocusable) {
					firstFocusable.focus();
				}
			} else {
				this.quickActionsCarouselRef.current.focus();
			}
		}
	}

	getApplicableQuickActionData = (quickActionsData) =>
		// Show only if we have icon and action url
		quickActionsData.filter((quickAction) => quickAction.enabled && quickAction.id && quickAction.actionUrl);

	onClickSeeAll = () => {
		let viewAllUrl = this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.viewAll;
		if (!viewAllUrl) {
			return;
		}
		const {
			hostInfo: { surface, client },
		} = this.searchHostContext;
		let trackingParams = '';
		if (surface === SEARCH_CCD_CCH_SAMCAP_CGEN.ccdHost) {
			trackingParams = `${SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForPreSearchQuickActionViewAll}&${SEARCH_CCD_CCH_SAMCAP_CGEN.samcapId}`;
		} else if (client === SEARCH_CCD_CCH_SAMCAP_CGEN.cchClient) {
			trackingParams = SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForPreSearchQuickActionViewAll;
		}

		viewAllUrl = viewAllUrl.includes('?') ? `${viewAllUrl}&${trackingParams}` : `${viewAllUrl}?${trackingParams}`;
		AnalyticsUtils.location = 'Search:view-all:Popular Adobe Express QuickActions';
		AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
		viewAllUrl = AnalyticsUtils.appendUrlWithAnalytics(viewAllUrl);
		this.analytics.postSeeAll('popularExpressQuickActions', viewAllUrl);
		this.onClickShowAllBase(this.searchHostContext, APP_TYPES.SPARK_TEMPLATES, {
			openUrl: viewAllUrl,
		});
	};
	renderTitle = () => {
		const headerTextId = 'app.search.presearch.quickActions.title';

		return (
			<div className='search-dropdown-quick-action-title-container'>
				<span className='search-dropdown-quick-actions-title'>
					<h5 id='search-dropdown-quick-actions-title-text' role='presentation'>
						<FormattedMessage id={headerTextId} />
					</h5>
				</span>
			</div>
		);
	};

	renderViewAll = () => {
		const viewAllUrl = this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.viewAll;
		const translationId = 'app.search.see_more';
		const headerTextId = 'app.search.presearch.quickActions.title';
		const ariaLabel = `${this.props.intl.formatMessage({ id: translationId })} ${this.props.intl.formatMessage({ id: headerTextId })}`;

		return (
			viewAllUrl && (
				<span
					className='search-dropdown-see-all-text see-all-text'
					onKeyDown={(event) => SearchUtils.onKeyDown(event)}
					tabIndex={-1}
					onClick={this.onClickSeeAll}
					role='menuitem'
					id='search-dropdown-quick-action-see-more'
					aria-label={ariaLabel}
				>
					<span className='all-text link-out-tag'>
						<FormattedMessage id={translationId} />
					</span>
				</span>
			)
		);
	};

	onCarouselClick = (e: any) => {
		this.analytics.postPreSearchCarouselScroll(e, 'popularExpressQuickActions');
	};

	makeChildrenFocusable = () => {
		this.setState({ areChildrenFocusable: true });
	};

	makeChildrenBlurred = () => {
		this.setState({ areChildrenFocusable: false });
	};

	keyDownHandler = (event: Record<string, any>) => {
		const key = event.key;
		const keyCode = event.keyCode;

		if (key === 'Enter' || keyCode === 13) {
			this.makeChildrenFocusable();
		} else if (key === 'Escape' || keyCode === 27) {
			if (this.state.areChildrenFocusable) {
				event.stopPropagation();
				event.preventDefault();
				this.makeChildrenBlurred();
			}
		}
	};

	renderQuickActions = () => {
		const quickActionsData =
			this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.resultItems || [];
		const applicableQuicActionsData = this.getApplicableQuickActionData(quickActionsData);
		if (!applicableQuicActionsData?.length) {
			return null;
		}

		this.analytics.postPreSearchItemAction({
			eventType: 'render',
			recommendationType: 'popularExpressQuickActions',
			uiSortOrder: PRESEARCH_UI_SORT_ORDERS.popularExpressQuickActions,
			data: applicableQuicActionsData,
		});
		const quickActionItems = applicableQuicActionsData.map((quickAction, index) => (
			<SearchDropDownQuickActionsItem
				key={quickAction.id}
				data={quickAction}
				index={index}
				count={applicableQuicActionsData.length}
				lastCard={index === applicableQuicActionsData.length - 1}
				tabIndex={this.state.areChildrenFocusable}
			/>
		));

		return (
			<>
				<div
					className='search-dropdown-quick-action-container'
					role='menu'
					aria-labelledby='search-dropdown-quick-actions-title-text'
				>
					<div className='search-dropdown-quick-action-header text-container'>
						{this.renderTitle()}
						{this.renderViewAll()}
					</div>
					<div id='search-dropdown-quick-action-desc' className='sr-only'>
						{`${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.enter',
						})} ${this.props.intl.formatMessage({
							id: 'app.search.presearch.quickActions.title',
						})} ${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.exit',
						})}`}
					</div>
					<div
						className='search-dropdown-quick-action'
						id='search-dropdown-quick-action'
						role='menuitem'
						tabIndex={-1}
						onKeyDown={this.keyDownHandler}
						ref={this.quickActionsCarouselRef}
						aria-describedby='search-dropdown-quick-action-desc'
					>
						<Carousel
							isCCX={SearchUtils.applyBorderBox()}
							darkMode={SearchUtils.isDarkMode(this.props.theme)}
							actionEvents={this.onCarouselClick}
							carouselBtnLabels={{
								previous: 'App quick actions',
								next: 'App quick actions',
							}}
							keepWidth
						>
							{quickActionItems}
						</Carousel>
					</div>
				</div>
				<HorizontalRuler />
			</>
		);
	};

	render(): ReactNode | null {
		return this.renderQuickActions();
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	theme: search.theme,
	presearchResultsPayload: search.presearchResultsPayload,
	searchAppMetadataList: search.searchAppMetadataList,
});
const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropDownQuickActions));
